import { useState, useEffect } from 'react';
import { api } from '../../api/client';

const today = new Date().toISOString().slice(0, 10);

function fmt(n) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

function fmtDate(str) {
  if (!str) return '';
  return new Date(str + 'T12:00:00').toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

const EMPTY_FORM = { date: today, amount: '', description: '', counterparty: '' };

const inp = {
  width: '100%',
  padding: '0.45rem 0.65rem',
  borderRadius: 7,
  border: '1px solid #ddd',
  fontSize: '0.875rem',
  fontFamily: "'Raleway', sans-serif",
  background: '#fff',
  color: '#333',
  boxSizing: 'border-box',
  outline: 'none',
};

const fieldLabel = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: '#666',
  marginBottom: '0.28rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

// ─── Transaction panel (reused for income and expense) ────────────────────────

function TransactionPanel({
  title, accentColor, btnColor, counterpartyLabel, buttonLabel,
  form, setForm, entries, saving, onSubmit, onDelete,
  isMobileLayout,
}) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      padding: '1.2rem 1.25rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    }}>
      <h3 style={{
        margin: 0,
        fontSize: '0.95rem',
        fontWeight: 700,
        color: accentColor,
        fontFamily: "'Poppins', sans-serif",
        paddingBottom: '0.65rem',
        borderBottom: `2px solid ${accentColor}28`,
      }}>
        {title}
      </h3>

      {/* Form */}
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr', gap: '0.55rem' }}>
          <div>
            <span style={fieldLabel}>Date</span>
            <input
              type="date"
              style={inp}
              value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              required
            />
          </div>
          <div>
            <span style={fieldLabel}>Amount (£)</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              style={inp}
              placeholder="0.00"
              value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              required
            />
          </div>
        </div>

        <div>
          <span style={fieldLabel}>Description</span>
          <input
            type="text"
            style={inp}
            placeholder="What was this for?"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            required
          />
        </div>

        <div>
          <span style={fieldLabel}>{counterpartyLabel}</span>
          <input
            type="text"
            style={inp}
            placeholder="Name or company"
            value={form.counterparty}
            onChange={e => setForm(f => ({ ...f, counterparty: e.target.value }))}
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          style={{
            alignSelf: isMobileLayout ? 'stretch' : 'flex-start',
            padding: '0.5rem 1.1rem',
            background: saving ? '#aaa' : btnColor,
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            fontWeight: 700,
            fontSize: '0.875rem',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: "'Raleway', sans-serif",
            transition: 'opacity 0.15s',
          }}
        >
          {saving ? 'Adding…' : buttonLabel}
        </button>
      </form>

      {/* Entry list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: 280, overflowY: 'auto' }}>
        {entries.length === 0 ? (
          <p style={{ color: '#bbb', fontSize: '0.82rem', margin: 0, textAlign: 'center', padding: '0.65rem 0' }}>
            No entries yet
          </p>
        ) : entries.map(t => (
          <div
            key={t.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.45rem 0.6rem',
              background: '#fafafa',
              borderRadius: 7,
              border: '1px solid #f0f0f0',
              fontSize: '0.82rem',
            }}
          >
            <span style={{ color: '#999', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 72 }}>
              {fmtDate(t.date)}
            </span>
            <span style={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#333',
            }}>
              {t.description}
            </span>
            {t.counterparty && (
              <span style={{
                color: '#aaa',
                flexShrink: 0,
                maxWidth: 90,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {t.counterparty}
              </span>
            )}
            <span style={{ fontWeight: 700, color: accentColor, flexShrink: 0 }}>
              {fmt(t.amount)}
            </span>
            <button
              onClick={() => onDelete(t.id)}
              title="Delete"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#ccc',
                fontSize: '1.1rem',
                lineHeight: 1,
                padding: '0 0.15rem',
                flexShrink: 0,
              }}
              onMouseOver={e => (e.currentTarget.style.color = '#e74c3c')}
              onMouseOut={e => (e.currentTarget.style.color = '#ccc')}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function BasicFinancialSummary({ setFilter, onUpgrade, readOnly = false }) {
  const isMobileLayout = typeof window !== 'undefined'
    && (window.innerWidth <= 1024 || window.matchMedia('(pointer: coarse)').matches);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incomeForm, setIncomeForm] = useState({ ...EMPTY_FORM });
  const [expenseForm, setExpenseForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState({ income: false, expense: false });

  useEffect(() => {
    let cancelled = false;
    api('/api/basic-transactions')
      .then(r => r.ok ? r.json() : { transactions: [] })
      .then(data => { if (!cancelled) setTransactions(data.transactions || []); })
      .catch(() => { })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // In readOnly mode, scope totals to the current calendar month only
  const thisMonth = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  const visibleTx = readOnly
    ? transactions.filter(t => (t.date || '').slice(0, 7) === thisMonth)
    : transactions;
  const income = visibleTx.filter(t => t.type === 'income');
  const expenses = visibleTx.filter(t => t.type === 'expense');
  const totalIn = income.reduce((s, t) => s + t.amount, 0);
  const totalOut = expenses.reduce((s, t) => s + t.amount, 0);
  const balance = totalIn - totalOut;

  async function addTransaction(type, form, setForm, key) {
    if (!form.amount || !form.description || !form.date) return;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const res = await api('/api/basic-transactions', {
        method: 'POST',
        body: JSON.stringify({
          type,
          amount: parseFloat(form.amount),
          description: form.description.trim(),
          counterparty: form.counterparty.trim(),
          date: form.date,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setTransactions(prev =>
          [data.transaction, ...prev].sort((a, b) =>
            b.date.localeCompare(a.date) || b.createdAt?.localeCompare(a.createdAt || '') || 0
          )
        );
        setForm({ ...EMPTY_FORM });
      }
    } catch (_) { }
    setSaving(s => ({ ...s, [key]: false }));
  }

  async function deleteTransaction(id) {
    try {
      const res = await api(`/api/basic-transactions/${id}`, { method: 'DELETE' });
      if (res.ok) setTransactions(prev => prev.filter(t => t.id !== id));
    } catch (_) { }
  }

  const tiles = [
    { label: 'Money In', value: totalIn, color: '#1e6645', bg: '#f0fdf4', border: '#bbf7d0' },
    { label: 'Money Out', value: totalOut, color: '#A12710', bg: '#fff7f5', border: '#fecaca' },
    {
      label: 'Balance',
      value: balance,
      color: balance >= 0 ? '#1e6645' : '#A12710',
      bg: balance >= 0 ? '#f0fdf4' : '#fff7f5',
      border: balance >= 0 ? '#bbf7d0' : '#fecaca',
    },
  ];

  return (
    <div style={{ padding: isMobileLayout ? '0.75rem' : '2rem', fontFamily: "'Raleway', sans-serif", maxWidth: isMobileLayout ? '100%' : 960 }}>

      {/* Header */}
      <h2 style={{
        margin: '0 0 0.2rem',
        color: '#0C2D48',
        fontFamily: "'Poppins', sans-serif",
        fontSize: '1.4rem',
        fontWeight: 700,
      }}>
        Financial Overview
      </h2>
      <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 1.75rem' }}>
        {readOnly
          ? `This month's summary — ${new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`
          : 'Track your income and expenses'}
      </p>

      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {tiles.map(({ label, value, color, bg, border }) => (
          <div key={label} style={{
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: 12,
            padding: '1.25rem 1rem',
          }}>
            <div style={{
              fontSize: '0.7rem',
              fontWeight: 700,
              color: '#aaa',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: '0.5rem',
            }}>
              {label}
            </div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 900,
              color,
              fontFamily: "'Poppins', sans-serif",
            }}>
              {loading ? <span style={{ color: '#ccc' }}>—</span> : fmt(value)}
            </div>
          </div>
        ))}
      </div>

      {/* Transaction logger — hidden in readOnly (Basic plan) mode */}
      {!readOnly && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <TransactionPanel
            title="Money In"
            accentColor="#1e6645"
            btnColor="#16a34a"
            counterpartyLabel="From (who paid you)"
            buttonLabel="+ Add Income"
            form={incomeForm}
            setForm={setIncomeForm}
            entries={income}
            saving={saving.income}
            isMobileLayout={isMobileLayout}
            onSubmit={e => { e.preventDefault(); addTransaction('income', incomeForm, setIncomeForm, 'income'); }}
            onDelete={deleteTransaction}
          />
          <TransactionPanel
            title="Money Out"
            accentColor="#A12710"
            btnColor="#dc2626"
            counterpartyLabel="To (who you paid)"
            buttonLabel="+ Add Expense"
            form={expenseForm}
            setForm={setExpenseForm}
            entries={expenses}
            saving={saving.expense}
            isMobileLayout={isMobileLayout}
            onSubmit={e => { e.preventDefault(); addTransaction('expense', expenseForm, setExpenseForm, 'expense'); }}
            onDelete={deleteTransaction}
          />
        </div>
      )}

      {/* Invoice upsell card */}
      <div style={{
        background: '#f5f3ff',
        border: '1px solid #ddd6fe',
        borderRadius: 14,
        padding: '1.5rem 1.75rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem',
        flexDirection: isMobileLayout ? 'column' : 'row',
      }}>
        <div style={{ fontSize: '1.75rem', flexShrink: 0 }}>🧾</div>
        <div>
          <h3 style={{
            margin: '0 0 0.35rem',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#0C2D48',
            fontFamily: "'Poppins', sans-serif",
          }}>
            Want to create and send professional invoices?
          </h3>
          <p style={{ margin: '0 0 1rem', color: '#555', fontSize: '0.875rem', lineHeight: 1.55 }}>
            Upgrade to Pro for full invoicing, expense tracking with receipt uploads, financial charts,
            VAT summaries, and cash flow reporting.
          </p>
          <button
            onClick={() => {
              if (onUpgrade) {
                onUpgrade('pro');
                return;
              }
              if (setFilter) setFilter('Settings');
            }}
            style={{
              padding: '0.6rem 1.4rem',
              background: '#0C2D48',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontFamily: "'Raleway', sans-serif",
            }}
          >
            Create Invoice — Upgrade to Pro
          </button>
        </div>
      </div>

      {/* Full-suite upgrade prompt (moved below transaction logger) */}
      <div style={{
        background: '#f1f5f9',
        borderRadius: 14,
        padding: '1.75rem',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem',
        flexDirection: isMobileLayout ? 'column' : 'row',
      }}>
        <div style={{ fontSize: '1.75rem', flexShrink: 0 }}>📊</div>
        <div>
          <h3 style={{
            margin: '0 0 0.4rem',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#0C2D48',
            fontFamily: "'Poppins', sans-serif",
          }}>
            Upgrade to Pro for the full financial suite
          </h3>
          <p style={{ margin: '0 0 1rem', color: '#555', fontSize: '0.875rem', lineHeight: 1.5 }}>
            Pro includes invoice creation &amp; sending, expense tracking, financial charts, VAT summaries,
            cash flow timeline, and full reporting.
          </p>
          <button
            onClick={() => {
              if (onUpgrade) {
                onUpgrade('pro');
                return;
              }
              if (setFilter) setFilter('Settings');
            }}
            style={{
              padding: '0.6rem 1.5rem',
              background: '#0C2D48',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontFamily: "'Raleway', sans-serif",
            }}
          >
            Upgrade to Pro
          </button>
        </div>
      </div>
    </div>
  );
}
